import {IFocusActivator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {MeshBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshBounds';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import bind from 'bind-decorator';
import {action, computed, observable} from 'mobx';
import {IMeshStateDetailsDesignUIState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';
import {TextSelection} from '@mintdata-wsp/mint-kit/api/models/Text';

export interface IMousePoint
{
    x:number,
    y:number,
}

export class MeshStateDetails implements IMeshStateDetailsDesignUIState
{
    @observable private _meshState:MeshState = null;

    @observable public activeCell:MeshLocation = null;
    @observable public cellAtCursor:MeshLocation = null;
    @observable public selectionRangeFrom:MeshLocation = null;
    @observable public selectionRangeTo:MeshLocation = null;
    @observable public extendRangeTo:MeshLocation = null;
    @observable public isSelectRangeMode:boolean = false;
    @observable public isExtendRangeMode:boolean = false;
    @observable public scrollWhenMouseOutsideSpeed:number = 0;
    @observable public mouseOutsideMeshPosition:IMousePoint = null;
    @observable public viewFocusActivator:IFocusActivator = null;
    @observable public visibleWidth:number = 0;
    @observable public visibleHeight:number = 0;
    @observable public scrollLeft:number = 0;
    @observable public scrollTop:number = 0;
    @observable public fixedRowIndex:number = null;

    @observable public isReadonly:boolean = false;
    @observable public isEditNoteMode:boolean = false;
    @observable public isScrolling:boolean = false;

    @observable public resizingColIndex:number = null;
    @observable public resizingColWidth:number = null;
    @observable public resizingRowIndex:number = null;
    @observable public resizingRowHeight:number = null;

    @observable public inputSelection:TextSelection = null;

    public constructor(meshState:MeshState)
    {
        this._meshState = meshState;
        this.activeCell = new MeshLocation(0, 0);
        this.selectionRangeFrom = this.activeCell;
        this.selectionRangeTo = this.activeCell;
    }

    @bind
    toJson():MeshStateDetails
    {
        return {
            activeCell: this.activeCell ? this.activeCell.toJson() : null,
            selectionRangeFrom: this.selectionRangeFrom ? this.selectionRangeFrom.toJson() : null,
            selectionRangeTo: this.selectionRangeTo ? this.selectionRangeTo.toJson() : null,
            extendRangeTo: this.extendRangeTo ? this.extendRangeTo.toJson() : null,
            isSelectRangeMode: this.isSelectRangeMode,
            isExtendRangeMode: this.isExtendRangeMode,
            visibleWidth: this.visibleWidth,
            visibleHeight: this.visibleHeight,
            scrollLeft: this.scrollLeft,
            scrollTop: this.scrollTop,
            isReadonly: this.isReadonly,
            isEditNoteMode: this.isEditNoteMode
        } as any;
    }

    public toDesignUIState(): IMeshStateDetailsDesignUIState {
        return {
            activeCell: this.activeCell,
            selectionRangeFrom: this.selectionRangeFrom,
            selectionRangeTo: this.selectionRangeTo,
            scrollLeft: this.scrollLeft,
            scrollTop: this.scrollTop
        }
    }

    public fromDesignUIState(state:IMeshStateDetailsDesignUIState)
    {
        if( state.activeCell )
            this.activeCell.fromDesignUIState(state.activeCell);

        if( state.selectionRangeFrom )
            this.selectionRangeFrom.fromDesignUIState(state.selectionRangeFrom);

        if( state.selectionRangeTo )
            this.selectionRangeTo.fromDesignUIState(state.selectionRangeTo);

        this.scrollLeft = state.scrollLeft;
        this.scrollTop = state.scrollTop;
    }

    @action
    public setMeshState(meshState:MeshState):void
    {
        this._meshState = meshState;
    }

    @computed
    public get selectedRange():MeshRange
    {
        if( !this._meshState || !this.selectionRangeFrom || !this.selectionRangeTo )
            return null;

        let {colIndex: colIndexFrom, rowIndex: rowIndexFrom} = this.selectionRangeFrom;
        let {colIndex: colIndexTo, rowIndex: rowIndexTo} = this.selectionRangeTo;

        const isColumnsSelection:boolean = colIndexFrom == null;
        const isRowsSelection:boolean = rowIndexFrom == null;

        if( colIndexFrom > colIndexTo )
            [colIndexFrom, colIndexTo] = [colIndexTo, colIndexFrom];

        if( rowIndexFrom > rowIndexTo )
            [rowIndexFrom, rowIndexTo] = [rowIndexTo, rowIndexFrom];

        return new MeshRange(
            new MeshLocation(
                colIndexFrom,
                rowIndexFrom
            ),
            new MeshLocation(
                !isColumnsSelection
                    ? colIndexTo != null
                        ? colIndexTo
                        : colIndexFrom
                    : null,
                !isRowsSelection
                    ? rowIndexTo != null
                        ? rowIndexTo
                        : rowIndexFrom
                    : null
            )
        );
    }

    @computed
    public get extendedRange():MeshRange
    {
        if( !this._meshState
            || !this.isExtendRangeMode
            || !this.selectedRange
            || !this.extendRangeTo )
            return null;

        const {
            from: {
                colIndex: colIndexFrom,
                rowIndex: rowIndexFrom
            },
            to: {
                colIndex: colIndexTo,
                rowIndex: rowIndexTo
            }
        } = this.selectedRange;
        const {colIndex: extendColIndexTo, rowIndex: extendRowIndexTo} = this.extendRangeTo;

        let colDeltaFrom:number = 0;
        let rowDeltaFrom:number = 0;
        let colDeltaTo:number = 0;
        let rowDeltaTo:number = 0;

        if( extendColIndexTo < colIndexFrom )
            colDeltaFrom = colIndexFrom - extendColIndexTo;
        else if( extendColIndexTo > colIndexTo )
            colDeltaTo = extendColIndexTo - colIndexTo;

        if( extendRowIndexTo < rowIndexFrom )
            rowDeltaFrom = rowIndexFrom - extendRowIndexTo;
        else if( extendRowIndexTo > rowIndexTo )
            rowDeltaTo = extendRowIndexTo - rowIndexTo;

        if( colDeltaFrom + colDeltaTo > rowDeltaFrom + rowDeltaTo )
            rowDeltaFrom = rowDeltaTo = 0;
        else
            colDeltaFrom = colDeltaTo = 0;

        if( colDeltaFrom === 0
            && rowDeltaFrom === 0
            && colDeltaTo === 0
            && rowDeltaTo === 0 )
            return null;

        return new MeshRange(
            new MeshLocation(
                colIndexFrom - colDeltaFrom,
                rowIndexFrom - rowDeltaFrom
            ),
            new MeshLocation(
                colIndexTo + colDeltaTo,
                rowIndexTo + rowDeltaTo
            )
        );
    }

    @computed
    public get activeCellBounds():MeshBounds
    {
        if( !this._meshState || !this.activeCell )
            return null;

        return MeshUtils.getRangeBounds(this.activeCell, this.activeCell, this._meshState);
    }

    @computed
    public get selectedRangeBounds():MeshBounds
    {
        if( !this.selectedRange )
            return null;

        return MeshUtils.getRangeBounds(this.selectedRange.from, this.selectedRange.to, this._meshState);
    }

    @computed
    public get extendedRangeBounds():MeshBounds
    {
        if( !this.extendedRange )
            return null;

        return MeshUtils.getRangeBounds(this.extendedRange.from, this.extendedRange.to, this._meshState);
    }
}
